import { useParams } from 'react-router-dom'
import { useEffect, useMemo, Suspense } from 'react'
import { getAppComponent } from '@/apps/_registry'
import { useEntitlements } from '@/hooks/useEntitlements'
import { useAuth } from '@/contexts/AuthContext'
import { broadcastSync } from '@/services/session/BroadcastSync'
import { colors, typography } from '@/lib/tokens'
import { DEFAULT_APPS } from '@/types/focus-shell'

export default function PopoutPage() {
  const { appId, instanceId } = useParams<{ appId: string; instanceId: string }>()

  const { user } = useAuth()
  const { isLoading: entitlementsLoading, enableUat, orgParams } = useEntitlements()

  const windowId = typeof window !== 'undefined' ? window.name : ''

  const appMetadata = useMemo(() => {
    return DEFAULT_APPS.find(app => app.appId === appId)
  }, [appId])

  // Set dynamic title for pop-out window
  useEffect(() => {
    if (appMetadata) {
      document.title = appMetadata.name
    }
  }, [appMetadata])

  // Notify shell that window is ready
  useEffect(() => {
    if (windowId) {
      broadcastSync.postMessage({ type: 'POPOUT_READY', windowId })
      console.log(`[PopoutPage] Window ready: ${windowId}`)
    }
  }, [windowId])

  const centeredStyle = {
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: colors.background,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.sans,
    fontSize: typography.fontSize.sm,
  } as const

  if (entitlementsLoading) {
    return (
      <div style={centeredStyle}>
        Loading {appMetadata?.name || appId}...
      </div>
    )
  }

  const AppComponent = getAppComponent(appId!)

  if (!AppComponent) {
    return (
      <div style={{ ...centeredStyle, fontSize: typography.fontSize.base }}>
        App not found: {appId}
      </div>
    )
  }

  return (
    <div style={{
      height: '100vh',
      width: '100vw',
      overflow: 'hidden',
      background: colors.background
    }}>
      <Suspense fallback={
        <div style={centeredStyle}>
          Loading {appMetadata?.name || appId}...
        </div>
      }>
        <AppComponent
          instanceId={instanceId!}
          user={user ? { uid: user.uid, email: user.email || '', displayName: user.displayName || '' } : undefined}
          getToken={user ? () => user.getIdToken() : undefined}
          enableUat={enableUat}
          orgParams={orgParams}
        />
      </Suspense>
    </div>
  )
}
